// Bulk scan's session: the items filed since this scan started. Pure functions over a plain
// object, so the pill and the list are derived, never hand-kept.
//
// item: {key, productId|null, name, title|null, barcode, kind: catalog|new|nobarcode,
//        pictureUrl, images[], thumbs[], uploads: [{id, n, state: uploading|uploaded|failed, lost?}]}
// An item's state is derived from its uploads, so "Add more photos" is one more upload on it.

const STORE_KEY = "cosmos.capture.bulk";

export const emptySession = (now = Date.now()) => ({ startedAt: now, items: [] });

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const itemPhotos = (item) => item.uploads.reduce((n, u) => n + u.n, 0);

export function itemState(item) {
  if (item.uploads.some((u) => u.state === "failed")) return "failed";
  if (item.uploads.some((u) => u.state === "uploading")) return "uploading";
  return "uploaded";
}

const mapItems = (s, fn) => ({ ...s, items: s.items.map(fn) });
const mapUploads = (s, fn) => mapItems(s, (it) => ({ ...it, uploads: it.uploads.map((u) => fn(u, it)) }));

/** A newly handed-over item. The first upload's id is its key. */
export function addItem(s, item, upload) {
  const it = { productId: null, title: null, pictureUrl: null, images: [], thumbs: [], addedAt: Date.now(), ...item, key: upload.id, uploads: [{ ...upload, state: "uploading" }] };
  return { ...(s.items.length ? s : { ...s, startedAt: Date.now() }), items: [...s.items, it] };
}

/** One more commit on an item already in the list. */
export function addUpload(s, key, upload) {
  return mapItems(s, (it) => (it.key === key ? { ...it, uploads: [...it.uploads, { ...upload, state: "uploading" }] } : it));
}

export const patchItem = (s, key, patch) => mapItems(s, (it) => (it.key === key ? { ...it, ...patch } : it));

export const removeItem = (s, key) => ({ ...s, items: s.items.filter((it) => it.key !== key) });

/** The queue committed this upload; `patch` carries what the commit learnt (productId, name), `stored` the server's photo count. */
export function fileUpload(s, id, patch = {}, stored = null) {
  return mapItems(s, (it) =>
    it.uploads.some((u) => u.id === id)
      ? { ...it, ...patch, uploads: it.uploads.map((u) => (u.id === id ? { ...u, state: "uploaded", ...(Number.isFinite(stored) ? { n: stored } : {}) } : u)) }
      : it,
  );
}

/**
 * Follow the queue's parked head: `stuck` is {uploadId, network} or null. A head parked by a
 * lost connection is waiting, not failed; a head parked by a refusal is failed; a failed upload
 * no longer parked is going again. Uploads lost to a reload stay failed.
 */
export function syncStuck(s, stuck) {
  return mapUploads(s, (u) => {
    if (u.lost || u.state === "uploaded") return u;
    const failed = Boolean(stuck && !stuck.network && stuck.uploadId === u.id);
    return failed === (u.state === "failed") ? u : { ...u, state: failed ? "failed" : "uploading" };
  });
}

/** After a reload the queue is empty: whatever was still going up is gone with the page. */
export const failLost = (s) => mapUploads(s, (u) => (u.state === "uploading" ? { ...u, state: "failed", lost: true } : u));

const sum = (arr, f) => arr.reduce((n, x) => n + f(x), 0);

export function counts(s) {
  const ups = s.items.flatMap((it) => it.uploads);
  return {
    items: s.items.length,
    photos: sum(ups, (u) => u.n),
    uploaded: sum(ups.filter((u) => u.state === "uploaded"), (u) => u.n),
    waiting: sum(ups.filter((u) => u.state === "uploading"), (u) => u.n),
    failed: sum(ups.filter((u) => u.state === "failed"), (u) => u.n),
    uploadingItems: s.items.filter((it) => itemState(it) === "uploading").length,
    retryable: ups.some((u) => u.state === "failed" && !u.lost),
  };
}

/**
 * The pill. Priority: red, amber, green (2 s after a save), up, dark; none before the first item.
 * saved: {n, name} while the green moment lasts. Returns {tone, main, sub, retry, ring}.
 */
export function pillState(s, { online = true, queueOffline = false, saved = null } = {}) {
  const c = counts(s);
  if (!c.items) return { tone: "none" };
  const main = plural(c.items, "item", "items");
  if (c.failed) return { tone: "red", main, sub: `${plural(c.failed, "photo", "photos")} didn't upload`, retry: c.retryable, ring: false };
  if (c.waiting && (!online || queueOffline)) return { tone: "amber", main, sub: `${plural(c.waiting, "photo", "photos")} waiting for signal`, retry: false, ring: false };
  if (saved) return { tone: "green", main: `✓ Item ${saved.n} saved`, sub: saved.name, retry: false, ring: false };
  if (c.uploadingItems) return { tone: "up", main, sub: `${c.uploadingItems} uploading`, retry: false, ring: true };
  return { tone: "dark", main, sub: plural(c.photos, "photo", "photos"), retry: false, ring: false };
}

/** The muted line under a row's name. */
export function rowLine(item) {
  const n = itemPhotos(item);
  const lead = item.kind === "catalog" ? `In catalog · ${plural(n, "new photo", "new photos")}` : `${item.kind === "nobarcode" ? "Photo only" : "New"} · ${plural(n, "photo", "photos")}`;
  const st = itemState(item);
  return { lead, tail: st === "uploading" ? "uploading" : st === "failed" ? `${plural(sum(item.uploads.filter((u) => u.state === "failed"), (u) => u.n), "photo", "photos")} didn't upload` : "", state: st };
}

/** The sheet's second line: "19 photos · 17 uploaded · started 10:42". */
export function headLine(s) {
  const c = counts(s);
  return `${plural(c.photos, "photo", "photos")} · ${c.uploaded} uploaded · started ${hhmm(s.startedAt)}`;
}

/** The clock time of a timestamp: "10:42". */
export function hhmm(ts) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Is a barcode just read already in this scan? A lookup that returned candidates matches on
 * productId; a new item (no productId yet) matches on its barcode. Returns {item, n} or null.
 */
export function findDuplicate(s, { barcode = null, candidates = [] } = {}) {
  const ids = new Set(candidates.map((c) => c.productId).filter(Boolean));
  let at = ids.size ? s.items.findIndex((it) => it.productId && ids.has(it.productId)) : -1;
  if (at < 0 && barcode) at = s.items.findIndex((it) => it.barcode === barcode && (it.kind === "new" || !it.productId));
  return at < 0 ? null : { item: s.items[at], n: at + 1 };
}

/** "You saved it as item 3 at 10:49" (no time for an item that predates the stamp). */
export const dupLine = (n, item) => `You saved it as item ${n}${item.addedAt ? " at " + hhmm(item.addedAt) : ""}`;

/** Coming back to an open scan: offered once per page load, and only with something in it. */
export const resumeOffer = (s, offered) => !offered && s.items.length >= 1;

/** The welcome-back sheet's two lines: "Started 10:42 · everything so far is saved", "7 items · 19 photos". */
export function resumeLines(s) {
  const c = counts(s);
  const sub = c.failed ? `${plural(c.failed, "photo", "photos")} didn't upload` : "everything so far is saved";
  const state = c.failed ? sub : c.waiting ? `${c.waiting} uploading` : "All uploaded";
  return {
    sub: `Started ${hhmm(s.startedAt)} · ${sub}`,
    main: `${plural(c.items, "item", "items")} · ${plural(c.photos, "photo", "photos")}`,
    state,
    bad: c.failed > 0,
  };
}

export function loadSession(storage) {
  try {
    const s = JSON.parse(storage.getItem(STORE_KEY) || "null");
    return s && Array.isArray(s.items) ? s : emptySession();
  } catch {
    return emptySession();
  }
}

export function saveSession(storage, s) {
  try {
    if (s.items.length) storage.setItem(STORE_KEY, JSON.stringify(s));
    else storage.removeItem(STORE_KEY);
  } catch {
    /* storage can be full or blocked; the list still works in memory */
  }
}
